import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { getVendors, createVendor, updateVendor, deleteVendor } from '../services/ClientService';
import ConfirmModal from './ConfirmModal';
import { toast } from 'react-toastify';

const Vendors = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);

    const [formData, setFormData] = useState({
        _id: '',
        vendorName: '',
        vendorMobile: '',
        vendorEmail: '',
        vendorLocation: ''
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchVendors(searchTerm);
        }, 500);
        
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchVendors = async (search = '') => {
        setLoading(true);
        try {
            const response = await getVendors({ searchTerm: search });
            setVendors(response.vendors || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            toast.error('Failed to fetch vendors');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (vendor = null) => {
        if (vendor) {
            setEditMode(true);
            setFormData({
                _id: vendor._id,
                vendorName: vendor.vendorName,
                vendorMobile: vendor.vendorMobile,
                vendorEmail: vendor.vendorEmail || '',
                vendorLocation: vendor.vendorLocation || ''
            });
        } else {
            setEditMode(false);
            setFormData({
                _id: '',
                vendorName: '',
                vendorMobile: '',
                vendorEmail: '',
                vendorLocation: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditMode(false);
        setFormData({
            _id: '',
            vendorName: '',
            vendorMobile: '',
            vendorEmail: '',
            vendorLocation: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.vendorName.trim() || !formData.vendorMobile.trim()) {
            toast.error('Vendor name and mobile are required');
            return;
        }

        setLoading(true);
        try {
            if (editMode) {
                await updateVendor(formData);
                toast.success('Vendor updated successfully');
            } else {
                await createVendor(formData);
                toast.success('Vendor created successfully');
            }
            handleCloseModal();
            fetchVendors(searchTerm);
        } catch (error) {
            console.error('Error saving vendor:', error);
            toast.error(error.response?.data?.error || 'Failed to save vendor');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRequest = (vendorId) => {
        setVendorToDelete(vendorId);
        setConfirmModalVisible(true);
    };

    const confirmDelete = async () => {
        setLoading(true);
        try {
            await deleteVendor(vendorToDelete);
            toast.success('Vendor deleted successfully');
            fetchVendors(searchTerm);
        } catch (error) {
            console.error('Error deleting vendor:', error);
            toast.error(error.response?.data?.error || 'Failed to delete vendor');
        } finally {
            setLoading(false);
            setConfirmModalVisible(false);
            setVendorToDelete(null);
        }
    };

    return (
        <div className="h-screen overflow-hidden bg-gray-50">
            {/* Header */}
            <div className="bg-white p-6 sticky top-0 z-10 shadow-sm">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <h1 className="text-3xl font-bold text-gray-800">Vendors</h1>
                    
                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                        {/* Search */}
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                        
                        {/* Add Vendor Button */}
                        <button
                            onClick={() => handleOpenModal()}
                            className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                            <FiPlus className="text-xl" />
                            Add Vendor
                        </button>
                    </div>
                </div>
            </div>

            {/* Vendors List */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                ) : vendors.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No vendors found</p>
                        <button
                            onClick={() => handleOpenModal()}
                            className="mt-4 text-blue-600 hover:text-blue-700 font-semibold"
                        >
                            Create your first vendor
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {vendors.map((vendor) => (
                            <div
                                key={vendor._id}
                                className="bg-white p-5 rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200"
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <h3 className="text-lg font-bold text-gray-800">{vendor.vendorName}</h3>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleOpenModal(vendor)}
                                            className="text-blue-600 hover:text-blue-700 p-1.5 rounded-full hover:bg-blue-50 transition-colors"
                                        >
                                            <FiEdit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteRequest(vendor._id)}
                                            className="text-red-600 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                                        >
                                            <FiTrash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-600 font-medium">Mobile:</span>
                                        <span className="text-gray-800">{vendor.vendorMobile}</span>
                                    </div>
                                    
                                    {vendor.vendorEmail && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-600 font-medium">Email:</span>
                                            <span className="text-gray-800 text-xs">{vendor.vendorEmail}</span>
                                        </div>
                                    )}
                                    
                                    {vendor.vendorLocation && (
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-600 font-medium">Location:</span>
                                            <span className="text-gray-800 flex-1">{vendor.vendorLocation}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Vendor Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-800">
                                {editMode ? 'Edit Vendor' : 'Add New Vendor'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <FiX className="text-2xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                {/* Vendor Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Vendor Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="vendorName"
                                        value={formData.vendorName}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                        placeholder="Enter vendor name"
                                        autoComplete="off"
                                        required
                                    />
                                </div>

                                {/* Vendor Mobile */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Mobile Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="vendorMobile"
                                        value={formData.vendorMobile}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                        placeholder="Enter mobile number"
                                        autoComplete="off"
                                        required
                                    />
                                </div>

                                {/* Vendor Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        name="vendorEmail"
                                        value={formData.vendorEmail}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                        placeholder="Enter email address"
                                        autoComplete="off"
                                    />
                                </div>

                                {/* Vendor Location */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Location/Address
                                    </label>
                                    <textarea
                                        name="vendorLocation"
                                        value={formData.vendorLocation}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                                        placeholder="Enter vendor location or address"
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            {/* Modal Actions */}
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : editMode ? 'Update Vendor' : 'Create Vendor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            <ConfirmModal
                visible={confirmModalVisible}
                message="Are you sure you want to delete this vendor? This action cannot be undone."
                onConfirm={confirmDelete}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    setVendorToDelete(null);
                }}
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
            />
        </div>
    );
};

export default Vendors;
